import { useEffect, useState } from 'react';

import axios from 'axios';
import { CarFront, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { usePublicStockCar } from '@/hooks/usePublicStockCar';
import { useBrandingStore } from '@/stores/branding.store';

import type { PublicStockCar } from '@/lib/api/public-stock.api';
import type { CSSProperties } from 'react';

const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap';
const DEFAULT_PRIMARY = '#0A7CFF';
/** Registry default for COMPANY_WEBSITE — treated as "not set". */
const PLACEHOLDER_WEBSITE = 'https://example.com';

const DETAIL_LABELS: Record<string, string> = {
  interior_colour: 'Interior colour',
  registration_date: 'First registered',
  mpg_combined: 'MPG (combined)',
  mpg_urban: 'MPG (urban)',
  mpg_extra_urban: 'MPG (extra urban)',
  co2: 'CO2 (g/km)',
  zero_to_60_seconds: '0-60 mph (s)',
  top_speed_mph: 'Top speed (mph)',
  torque_nm: 'Torque (Nm)',
  power_kw: 'Power (kW)',
  insurance_group: 'Insurance group',
  euro_status: 'Euro status',
  ev_range_miles: 'Electric range (miles)',
  ev_battery_kwh: 'Battery (kWh)',
};

const formatNumber = (value: number) => value.toLocaleString('en-GB');

const formatDetail = (key: string, value: string | number): string => {
  if (key === 'registration_date' && typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }
  return typeof value === 'number' ? formatNumber(value) : value;
};

const carTitle = (car: PublicStockCar) =>
  [car.year, car.manufacturer, car.model].filter(Boolean).join(' ') || car.title || 'Car details';

const keySpecs = (car: PublicStockCar): Array<[string, string]> => {
  const specs: Array<[string, string | null]> = [
    ['Mileage', car.mileage !== null ? `${formatNumber(car.mileage)} miles` : null],
    ['Year', car.year !== null ? String(car.year) : null],
    ['Fuel', car.fuel_type],
    ['Gearbox', car.transmission],
    ['Body', car.body_type],
    ['Engine', car.engine_size ? `${car.engine_size}L` : null],
    ['Power', car.bhp !== null ? `${car.bhp} BHP` : null],
    ['Drive', car.drive_train],
    ['Doors', car.doors !== null ? String(car.doors) : null],
    ['Seats', car.seats !== null ? String(car.seats) : null],
    ['Colour', car.colour],
    ['Owners', car.owners !== null ? String(car.owners) : null],
    ['Registration', car.registration],
    ['Condition', car.condition],
  ];
  return specs.filter((spec): spec is [string, string] => !!spec[1]);
};

const Gallery = ({ images, alt }: { images: string[]; alt: string }) => {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const usable = images.filter((url) => !failed.has(url));

  if (usable.length === 0) {
    return (
      <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-gray-100">
        <CarFront className="h-16 w-16 text-gray-300" />
      </div>
    );
  }

  const current = Math.min(index, usable.length - 1);
  const go = (step: number) => setIndex((current + step + usable.length) % usable.length);

  return (
    <div>
      <div className="relative overflow-hidden rounded-xl bg-gray-100">
        <img
          src={usable[current]}
          alt={alt}
          className="aspect-[4/3] w-full object-cover"
          onError={() => setFailed((prev) => new Set(prev).add(usable[current]))}
        />
        {usable.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={() => go(-1)}
              className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow"
            >
              <ChevronLeft className="h-5 w-5 text-gray-800" />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={() => go(1)}
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow"
            >
              <ChevronRight className="h-5 w-5 text-gray-800" />
            </button>
            <span className="absolute right-2 bottom-2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs text-white">
              {current + 1} / {usable.length}
            </span>
          </>
        )}
      </div>
      {usable.length > 1 && (
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          {usable.map((url, i) => (
            <button
              key={url}
              type="button"
              aria-label={`Photo ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`shrink-0 overflow-hidden rounded-md border-2 ${
                i === current ? 'border-[var(--wf-primary)]' : 'border-transparent'
              }`}
            >
              <img src={url} alt="" loading="lazy" className="h-14 w-20 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Message = ({ title, text }: { title: string; text: string }) => (
  <div className="mx-auto max-w-md py-24 text-center">
    <CarFront className="mx-auto h-12 w-12 text-gray-300" />
    <h1 className="mt-4 text-xl font-semibold text-[var(--wf-primary)]">{title}</h1>
    <p className="mt-2 text-gray-500">{text}</p>
  </div>
);

const CarView = ({ car }: { car: PublicStockCar }) => {
  const title = carTitle(car);
  const subtitle = car.derivative || car.trim;
  const details = Object.entries(car.details).filter(
    (entry): entry is [string, string | number] => !!DETAIL_LABELS[entry[0]] && entry[1] !== null,
  );
  const dealerArea = [car.dealer.town, car.dealer.postcode].filter(Boolean).join(', ');

  return (
    <div className="grid gap-6 md:grid-cols-[3fr_2fr]">
      <Gallery images={car.images} alt={title} />

      <div className="space-y-5">
        <div>
          <h1 className="text-2xl leading-tight font-semibold text-[var(--wf-primary)] md:text-3xl">
            {title}
          </h1>
          {subtitle && <p className="mt-1 text-gray-600">{subtitle}</p>}
        </div>

        {car.price !== null && (
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Cash price</p>
            <p className="text-3xl font-bold text-gray-900">
              £{formatNumber(car.price)}
              {car.price_excludes_vat && (
                <span className="ml-1 text-base font-medium text-gray-500">+ VAT</span>
              )}
            </p>
          </div>
        )}

        {(car.dealer.name || dealerArea) && (
          <div className="flex items-start gap-2 text-gray-700">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[var(--wf-pink)]" />
            <div>
              {car.dealer.name && <p className="font-medium">{car.dealer.name}</p>}
              {dealerArea && <p className="text-sm text-gray-500">{dealerArea}</p>}
            </div>
          </div>
        )}
      </div>

      <section className="md:col-span-2">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Key specs</h2>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {keySpecs(car).map(([label, value]) => (
            <div key={label} className="rounded-lg bg-[#F8F8F8] p-3">
              <dt className="text-xs text-gray-500">{label}</dt>
              <dd className="font-medium text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {details.length > 0 && (
        <section className="md:col-span-2">
          <h2 className="mb-3 text-lg font-semibold text-gray-900">More details</h2>
          <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {details.map(([key, value]) => (
              <div key={key} className="flex justify-between gap-4 px-4 py-2.5 text-sm">
                <dt className="text-gray-500">{DETAIL_LABELS[key]}</dt>
                <dd className="text-right font-medium text-gray-900">{formatDetail(key, value)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
};

const PublicStockCarPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: car, error, isLoading } = usePublicStockCar(id);
  const { companyName, logoUrl, websiteUrl, primaryColor } = useBrandingStore();

  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_URL}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_URL;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (car) document.title = `${carTitle(car)} | ${companyName || 'Car details'}`;
  }, [car, companyName]);

  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const homeUrl = websiteUrl && websiteUrl !== PLACEHOLDER_WEBSITE ? websiteUrl : undefined;
  const primary = primaryColor || DEFAULT_PRIMARY;
  const logo = logoUrl ? (
    <img src={logoUrl} alt={companyName || 'Logo'} className="h-10 w-auto md:h-12" />
  ) : (
    <span className="text-xl font-semibold text-[var(--wf-primary)]">{companyName}</span>
  );

  return (
    <div
      className="min-h-screen bg-white text-gray-900"
      style={
        {
          fontFamily: 'Poppins, sans-serif',
          '--wf-primary': primary,
          '--wf-pink': '#FE4D87',
        } as CSSProperties
      }
    >
      <header className="bg-white">
        <div className="mx-auto flex max-w-6xl items-center px-4 py-3">
          {homeUrl ? <a href={homeUrl}>{logo}</a> : logo}
        </div>
        <div
          className="h-2.5"
          style={{
            background: `linear-gradient(90deg, ${primary} 0%, #9145FD 50%, #FA6D64 100%)`,
          }}
        />
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        {isLoading && <p className="py-24 text-center text-gray-500">Loading car details…</p>}
        {status === 404 && (
          <Message
            title="This car is no longer available"
            text="It may have just been sold. Get in touch and we'll help you find a similar one."
          />
        )}
        {status === 429 && (
          <Message title="Please wait a moment" text="Too many requests. Try again in a minute." />
        )}
        {error && status !== 404 && status !== 429 && (
          <Message
            title="Something went wrong"
            text="We couldn't load this car. Please try again."
          />
        )}
        {car && <CarView car={car} />}
      </main>

      <footer className="mt-8 border-t border-gray-100 py-6 text-center text-xs text-gray-400">
        {companyName && (
          <p>
            © {new Date().getFullYear()} {companyName}
          </p>
        )}
        <p className="mt-1">
          Price, mileage and specification are supplied by the seller and may change.
        </p>
      </footer>
    </div>
  );
};

export default PublicStockCarPage;
